import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { MAX_LIMIT } from '@sas/nova-core/services';
import { getPreferences, setPreferences } from '@sas/nova-core/services/preferences';
import { useContentSortDispatch } from '../providers/ContentSortProvider';
import { CONTENT_PROVIDERS_LOGGER_COMPONENT } from '../util/__internal__/common';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = getLogger(CONTENT_PROVIDERS_LOGGER_COMPONENT);
const PREF_KEY_SAS_CONTENT_CURRENT_VIEW = 'sas.content.current.view';
const PREF_KEY_SAS_CONTENT_CURRENT_SORT = 'sas.content.current.sort';
const PREF_KEY_SAS_CONTENT_TABLE_COLUMNS = 'sas.content.table.columns';
export const ContentPreferencesContext = /*#__PURE__*/createContext({});
export const ContentPreferencesDispatch = /*#__PURE__*/createContext(undefined);

/**
 * Returns the current preferences state for the nearest provider, or undefined if not under a
 * ContentPreferencesProvider.
 */
export const useContentPreferences = () => useContext(ContentPreferencesContext);

/**
 * Dispatch a new preferences state to all components consuming this provider. Will return undefined
 * if not under a ContentPreferencesProvider.
 */
export const useContentPreferencesDispatch = () => useContext(ContentPreferencesDispatch);
/**
 * Provides preferences functionality to content components that consume this provider.
 * @param preferencesQualifier
 * @param children
 * @constructor
 */
export const ContentPreferencesProvider = ({
  preferencesQualifier,
  children
}) => {
  const [state, setState] = useState({
    tableColumns: null,
    currentSort: null
  });
  const finalPreferencesQualifier = preferencesQualifier ? preferencesQualifier.replace(/[%&/!#'()*+,:;=?@[\]{}|$]/g, '_') : undefined;
  const currentViewKey = finalPreferencesQualifier ? `${PREF_KEY_SAS_CONTENT_CURRENT_VIEW}<${finalPreferencesQualifier}>` : PREF_KEY_SAS_CONTENT_CURRENT_VIEW;
  const currentSortKey = finalPreferencesQualifier ? `${PREF_KEY_SAS_CONTENT_CURRENT_SORT}<${finalPreferencesQualifier}>` : PREF_KEY_SAS_CONTENT_CURRENT_SORT;
  const tableColumnsKey = finalPreferencesQualifier ? `${PREF_KEY_SAS_CONTENT_TABLE_COLUMNS}<${finalPreferencesQualifier}>` : PREF_KEY_SAS_CONTENT_TABLE_COLUMNS;
  const prefDispatch = useCallback(action => {
    setState(prevState => {
      const newState = typeof action === 'function' ? action(prevState) : {
        ...prevState,
        ...action
      };
      if ('currentView' in action && action.currentView && newState.currentView) {
        setPreferences([{
          id: currentViewKey,
          value: newState.currentView
        }]).catch(error => {
          logger.warn(`Unexpected error saving preference:currentViewKey=${currentViewKey}:error=${error}`);
        });
      }
      if ('currentSort' in action && action.currentSort && newState.currentSort) {
        setPreferences([{
          id: currentSortKey,
          value: JSON.stringify(newState.currentSort)
        }]).catch(error => {
          logger.warn(`Unexpected error saving preference:currentViewKey=${currentViewKey}:error=${error}`);
        });
      }
      if ('tableColumns' in action && action.tableColumns && newState.tableColumns) {
        setPreferences([{
          id: tableColumnsKey,
          value: JSON.stringify(newState.tableColumns)
        }]).catch(error => {
          logger.warn(`Unexpected error saving preference:currentViewKey=${currentViewKey}:error=${error}`);
        });
      }
      logger.info(`Preferences set:action=${JSON.stringify(action)}:state=${JSON.stringify(newState)}`);
      return newState;
    });
  }, [currentViewKey, currentSortKey, tableColumnsKey]);
  const didSetFromPrefsRef = useRef(false);
  const setSortInfo = useContentSortDispatch();
  useEffect(() => {
    if (didSetFromPrefsRef.current) return;
    getPreferences({
      params: {
        limit: MAX_LIMIT
      }
    }).then(prefs => {
      if (!didSetFromPrefsRef.current) {
        didSetFromPrefsRef.current = true;
        // If not initially set, use pref currentView if present, otherwise to null.
        // This is to avoid setting the currentView to tileView if the user has not set it.
        // Initial view will be set in ContentSelector and updated when the user selects a new view.
        if (!state.currentView) {
          const currentViewPref = prefs.items.find(pref => pref.id === currentViewKey);
          state.currentView = currentViewPref?.value ? currentViewPref?.value : null;
        }
        // If not initially set, use pref currentSort if present, otherwise no default.
        if (!state.currentSort) {
          const currentSortPref = prefs.items.find(pref => pref.id === currentSortKey);
          state.currentSort = currentSortPref?.value ? JSON.parse(currentSortPref.value) : null;
          if (state.currentSort) {
            setSortInfo?.({
              key: state.currentSort.field,
              direction: state.currentSort.direction
            });
          }
        }
        // If not initially set, use pref tableColumns if present, otherwise no default.
        if (!state.tableColumns) {
          const tableColumnsPref = prefs.items.find(pref => pref.id === tableColumnsKey);
          state.tableColumns = tableColumnsPref?.value ? JSON.parse(tableColumnsPref.value) : null;
        }
        setState({
          ...state
        });
        logger.info(`Preferences loaded:currentViewKey=${currentViewKey}:state=${JSON.stringify(state)}`);
      }
    }).catch(() => {
      if (!didSetFromPrefsRef.current && !state.currentView) {
        setState({
          currentView: 'tileView'
        });
        didSetFromPrefsRef.current = true;
      }
    });
  }, [state, currentViewKey, currentSortKey, tableColumnsKey, preferencesQualifier, setSortInfo]);
  return /*#__PURE__*/_jsx(ContentPreferencesContext.Provider, {
    value: state,
    children: /*#__PURE__*/_jsx(ContentPreferencesDispatch.Provider, {
      value: prefDispatch,
      children: children
    })
  });
};
//# sourceMappingURL=ContentPreferencesProvider.js.map